import React, { useState, useEffect, useRef, useCallback, createContext, useContext } from 'react';
import { Edit3, Save, X, LogOut, Eye, Loader2, CheckCircle2, Bold, Italic, Link2, History, RotateCcw, UploadCloud, Sparkles, Target } from 'lucide-react';
import { useAdminAuth } from '../hooks/useAdminAuth';
import {
  logout,
  setContent as saveContent,
  getContent as fetchContent,
  uploadImage,
  buildMediaUrl,
  listVersions as apiListVersions,
  revertVersion as apiRevert,
  aiSeo,
} from '../admin/adminApi';
import { useLanguage } from '../context/LanguageContext';
import { analyzeSeo } from '../lib/seoScore';

const EditCtx = createContext({});
export const useEditMode = () => useContext(EditCtx);

const LANGS = [
  { c: 'it', n: 'IT' }, { c: 'en', n: 'EN' }, { c: 'fr', n: 'FR' },
  { c: 'de', n: 'DE' }, { c: 'es', n: 'ES' },
];

// path helpers
const patchPath = (obj, path, value) => {
  if (!path) return value;
  const parts = path.split('.');
  const clone = obj ? JSON.parse(JSON.stringify(obj)) : (/^\d+$/.test(parts[0]) ? [] : {});
  let ref = clone;
  for (let i = 0; i < parts.length - 1; i++) {
    const k = parts[i];
    const next = parts[i + 1];
    if (ref[k] === undefined || ref[k] === null) ref[k] = /^\d+$/.test(next) ? [] : {};
    ref = ref[k];
  }
  ref[parts[parts.length - 1]] = value;
  return clone;
};

// Deep merge JSON. `over` overrides `base`. Arrays: `over` length wins (fallback tail dropped)
// so that CMS-deleted list items don't get resurrected from the static baseline on re-save.
const deepMerge = (base, over) => {
  if (over === undefined) return base;
  if (Array.isArray(over)) {
    if (Array.isArray(base)) return over.map((item, i) => deepMerge(base[i], item));
    return over.slice();
  }
  if (base && typeof base === 'object' && !Array.isArray(base) && over && typeof over === 'object') {
    const out = { ...base };
    for (const k of Object.keys(over)) out[k] = deepMerge(base[k], over[k]);
    return out;
  }
  return over;
};

const readAtPath = (obj, path) => {
  if (!path) return obj;
  return path.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
};

export const EditModeProvider = ({ children }) => {
  const authed = useAdminAuth();
  const { lang: appLang } = useLanguage();
  const [editMode, setEditMode] = useState(false);
  const [imageEditor, setImageEditor] = useState(null); // {section, path, value, label}
  const [inline, setInline] = useState(null);            // {section, path, value, label, type, lang, el, status}
  const [refreshTick, setRefreshTick] = useState(0);
  const refresh = () => setRefreshTick((t) => t + 1);

  // Per-section merged baselines registered by useCmsContent — used so
  // inline saves preserve sibling fields that only exist in the fallback.
  const baselinesRef = useRef({});
  const registerSectionBaseline = useCallback((section, data) => {
    baselinesRef.current[section] = data;
  }, []);

  useEffect(() => { if (!authed) { setEditMode(false); setInline(null); setImageEditor(null); } }, [authed]);

  // Start / stop inline
  const startInline = ({ section, path, value, label, type = 'multilang', el }) => {
    const isScalar = type === 'text' || typeof value === 'string';
    const initValue = isScalar
      ? (typeof value === 'string' ? value : '')
      : (value && typeof value === 'object' ? { ...value } : { [appLang]: '' });
    setInline({
      section,
      path,
      value: initValue,
      label,
      type: isScalar ? 'text' : 'multilang',
      lang: appLang,
      el,
      status: 'idle',
    });
  };

  const openImageEditor = (cfg) => setImageEditor(cfg);

  const setInlineEl = (el) => setInline((s) => (s ? { ...s, el } : s));

  const inlineRef = useRef(inline);
  useEffect(() => { inlineRef.current = inline; }, [inline]);

  const doSave = useCallback(async (state) => {
    if (!state) return;
    try {
      setInline((s) => s ? { ...s, status: 'saving' } : s);
      const backend = (await fetchContent(state.section).catch(() => null)) || {};
      const baseline = baselinesRef.current[state.section] || {};
      const start = deepMerge(baseline, backend);
      const patched = patchPath(start, state.path, state.value);
      await saveContent(state.section, patched);
      refresh();
      setInline((s) => s ? { ...s, status: 'saved' } : s);
      setTimeout(() => setInline((s) => (s && s.status === 'saved' ? { ...s, status: 'idle' } : s)), 1400);
    } catch (e) {
      console.error('inline save error', e);
      setInline((s) => s ? { ...s, status: 'error' } : s);
    }
  }, []);

  const debRef = useRef(null);
  const scheduleAutosave = (state) => {
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => doSave(state), 1500);
  };

  const updateInlineText = (newText) => {
    setInline((s) => {
      if (!s) return s;
      const next = s.type === 'text'
        ? { ...s, value: newText, status: 'dirty' }
        : { ...s, value: { ...s.value, [s.lang]: newText }, status: 'dirty' };
      scheduleAutosave(next);
      return next;
    });
  };

  const setInlineLang = (l) => {
    setInline((s) => (s ? { ...s, lang: l } : s));
  };

  const closeInline = async ({ commit = true } = {}) => {
    if (debRef.current) clearTimeout(debRef.current);
    const s = inlineRef.current;
    if (!s) return;
    // Only persist if the user actually made a change. Prevents accidental
    // overwrites when merely clicking-then-Escaping an editable.
    if (commit && s.status === 'dirty') await doSave(s);
    setInline(null);
  };

  // Close inline on Escape or click outside
  useEffect(() => {
    if (!inline) return;
    const onKey = (e) => { if (e.key === 'Escape') closeInline({ commit: true }); };
    const onDown = (e) => {
      const el = inline.el;
      if (!el) return;
      // ignore clicks inside toolbar or same element
      if (el.contains(e.target)) return;
      if (e.target.closest('[data-floating-toolbar]')) return;
      if (e.target.closest('[data-testid="edit-modal"]')) return;
      closeInline({ commit: true });
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
    };
    // eslint-disable-next-line
  }, [inline?.el]);

  return (
    <EditCtx.Provider
      value={{
        authed,
        editMode,
        refreshTick,
        refresh,
        startInline,
        closeInline,
        updateInlineText,
        setInlineLang,
        setInlineEl,
        inline,
        openImageEditor,
        registerSectionBaseline,
      }}
    >
      {children}
      {authed && <EditToolbar editMode={editMode} setEditMode={setEditMode} />}
      {authed && inline && <FloatingToolbar />}
      {imageEditor && (
        <ImageModal
          config={imageEditor}
          onClose={() => setImageEditor(null)}
          onSaved={refresh}
        />
      )}
    </EditCtx.Provider>
  );
};

const EditToolbar = ({ editMode, setEditMode }) => (
  <div className="fixed bottom-6 right-6 z-[60] bg-[#1B2F1B] text-white rounded-full shadow-2xl flex items-center gap-1 p-1.5 border border-white/20" data-testid="edit-toolbar">
    <button
      data-testid="toggle-edit-mode"
      onClick={() => setEditMode(!editMode)}
      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition ${editMode ? 'bg-white text-[#1B2F1B]' : 'hover:bg-white/10'}`}
    >
      {editMode ? <><Eye className="h-4 w-4" /> Anteprima</> : <><Edit3 className="h-4 w-4" /> Modifica</>}
    </button>
    <a href="/admin" className="px-3 py-2 rounded-full text-sm hover:bg-white/10 transition" data-testid="open-dashboard">Dashboard</a>
    <button onClick={logout} className="p-2 rounded-full hover:bg-white/10 transition" title="Esci" data-testid="logout-btn"><LogOut className="h-4 w-4" /></button>
  </div>
);

// ---- Floating toolbar ----
const FloatingToolbar = () => {
  const { inline, setInlineLang, closeInline, updateInlineText } = useEditMode();
  const [rect, setRect] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [versions, setVersions] = useState([]);

  // Track element position (scroll/resize/reflow)
  useEffect(() => {
    if (!inline?.el) return;
    let raf;
    const measure = () => {
      const r = inline.el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height, bottom: r.bottom });
      raf = requestAnimationFrame(measure);
    };
    raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
  }, [inline?.el]);

  const wrap = (before, after = before) => {
    const el = inline.el;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    if (!el.contains(range.startContainer)) return;
    const text = range.toString() || 'testo';
    const wrapped = before + text + after;
    document.execCommand('insertText', false, wrapped);
    // input event will trigger updateInlineText via Editable's onInput
  };

  const insertLink = () => {
    const url = window.prompt('URL del link (es. https://...)');
    if (!url) return;
    wrap('[', `](${url})`);
  };

  const loadVersions = async () => {
    try { setVersions(await apiListVersions(inline.section)); } catch { setVersions([]); }
  };
  useEffect(() => { if (showHistory) loadVersions(); /* eslint-disable-next-line */ }, [showHistory]);

  const doRevert = async (vId) => {
    if (!window.confirm('Ripristinare questa versione?')) return;
    try {
      await apiRevert(inline.section, vId);
      const current = await fetchContent(inline.section);
      const v = readAtPath(current, inline.path);
      const newVal = v && typeof v === 'object' ? v : { [inline.lang]: (typeof v === 'string' ? v : '') };
      updateInlineText(newVal[inline.lang] || '');
      // Also update local buffer for all langs
      // Not perfect but user can switch tab; refresh after close
    } catch (e) {
      alert('Errore ripristino: ' + (e.response?.data?.detail || e.message));
    }
  };

  if (!rect) return null;
  const top = Math.max(8, rect.top - 56);
  const left = Math.max(8, Math.min(window.innerWidth - 420, rect.left));

  return (
    <div
      data-floating-toolbar
      data-testid="floating-toolbar"
      className="fixed z-[70] bg-[#1B2F1B] text-white rounded-lg shadow-2xl border border-white/20 flex items-stretch overflow-hidden animate-fade-in-up"
      style={{ top, left }}
      onMouseDown={(e) => e.preventDefault()}
    >
      {/* Rich text */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-r border-white/10">
        <ToolBtn onClick={() => wrap('**')} title="Grassetto" testid="fb-bold"><Bold className="h-3.5 w-3.5" /></ToolBtn>
        <ToolBtn onClick={() => wrap('*')} title="Corsivo" testid="fb-italic"><Italic className="h-3.5 w-3.5" /></ToolBtn>
        <ToolBtn onClick={insertLink} title="Link" testid="fb-link"><Link2 className="h-3.5 w-3.5" /></ToolBtn>
      </div>

      {/* Languages (hidden for scalar text fields) */}
      {inline.type !== 'text' && (
        <div className="flex items-center gap-0.5 px-2 py-1.5 border-r border-white/10">
          {LANGS.map((l) => (
            <button
              key={l.c}
              data-testid={`fb-lang-${l.c}`}
              onClick={() => setInlineLang(l.c)}
              className={`px-2 py-1 rounded text-[11px] font-semibold transition ${inline.lang === l.c ? 'bg-white text-[#1B2F1B]' : 'hover:bg-white/10 text-white/85'}`}
            >
              {l.n}
              {(inline.value?.[l.c] || '').length > 0 && (
                <span className={`ml-1 inline-block w-1 h-1 rounded-full ${inline.lang === l.c ? 'bg-[#1B2F1B]' : 'bg-white/60'}`} />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Status */}
      <div className="flex items-center px-3 border-r border-white/10 min-w-[110px]">
        <StatusInline status={inline.status} />
      </div>

      {/* History */}
      <div className="relative">
        <button
          onClick={() => setShowHistory((s) => !s)}
          className={`h-full flex items-center gap-1 px-3 text-xs hover:bg-white/10 transition ${showHistory ? 'bg-white/10' : ''}`}
          data-testid="fb-history"
        >
          <History className="h-3.5 w-3.5" /> Storico
        </button>
        {showHistory && (
          <div className="absolute top-full right-0 mt-2 w-72 max-h-64 overflow-y-auto bg-white text-neutral-900 rounded shadow-2xl border border-neutral-200 divide-y divide-neutral-100" data-testid="fb-version-list">
            {versions.length === 0 && <div className="p-3 text-xs text-neutral-500 text-center">Nessuna versione</div>}
            {versions.map((v) => (
              <div key={v.id} className="flex items-center justify-between px-3 py-2 text-xs">
                <div>
                  <div className="font-medium">{new Date(v.saved_at).toLocaleString('it-IT')}</div>
                  <div className="text-neutral-500">{v.was_updated_by || '—'}</div>
                </div>
                <button onClick={() => doRevert(v.id)} className="flex items-center gap-1 px-2 py-1 border border-neutral-300 rounded hover:border-[#22C55E] hover:text-[#22C55E]" data-testid={`fb-revert-${v.id}`}>
                  <RotateCcw className="h-3 w-3" /> Ripristina
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* SEO score + AI */}
      <SeoAiPanel inline={inline} updateInlineText={updateInlineText} />

      {/* Close */}
      <button
        onClick={() => closeInline({ commit: true })}
        className="px-3 hover:bg-white/10 transition flex items-center"
        title="Chiudi (Esc)"
        data-testid="fb-close"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

const ToolBtn = ({ onClick, title, testid, children }) => (
  <button type="button" onClick={onClick} title={title} data-testid={testid} className="p-1.5 rounded hover:bg-white/10 transition">{children}</button>
);

// SEO score + AI actions
const SeoAiPanel = ({ inline, updateInlineText }) => {
  const [open, setOpen] = React.useState(false);
  const [keyword, setKeyword] = React.useState('');
  const [busy, setBusy] = React.useState(null); // 'rewrite' | 'meta'
  const [meta, setMeta] = React.useState(null);
  const [err, setErr] = React.useState('');

  const text = inline.type === 'text'
    ? (typeof inline.value === 'string' ? inline.value : '')
    : (inline.value?.[inline.lang] ?? '');
  const isTitleLike = /(^|\.)title$/.test(inline.path || '') || /title/i.test(inline.label || '');
  const isDescLike = /description|body|paragraphs|desc/i.test(inline.path || '') || /descrizione/i.test(inline.label || '');
  const seoType = isTitleLike ? 'title' : isDescLike ? 'body' : 'body';
  const analysis = React.useMemo(() => analyzeSeo(text, keyword, seoType), [text, keyword, seoType]);

  const runAi = async (mode) => {
    setBusy(mode); setErr('');
    try {
      const res = await aiSeo({ text, lang: inline.lang || 'it', keyword, mode });
      if (mode === 'rewrite') {
        if (res.text) updateInlineText(res.text);
      } else {
        setMeta({ title: res.title, description: res.description });
      }
    } catch (e) {
      setErr(e.response?.data?.detail || 'Errore AI');
    }
    setBusy(null);
  };

  const color = analysis.level === 'good' ? '#22C55E' : analysis.level === 'ok' ? '#E0B400' : '#E24C4B';

  return (
    <div className="relative border-l border-white/10">
      <button
        onClick={() => setOpen((s) => !s)}
        className={`h-full flex items-center gap-2 px-3 text-xs hover:bg-white/10 transition ${open ? 'bg-white/10' : ''}`}
        data-testid="fb-seo-toggle"
        title="Score SEO e AI"
      >
        <Target className="h-3.5 w-3.5" />
        <span className="flex items-center gap-1">
          SEO
          <span className="inline-flex items-center justify-center rounded-full text-[10px] font-bold px-1.5 py-0.5 min-w-[26px]" style={{ background: color, color: '#0A2A25' }} data-testid="fb-seo-score">
            {analysis.score}
          </span>
        </span>
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-2 w-96 bg-white text-neutral-900 rounded shadow-2xl border border-neutral-200 p-4 z-[75]" data-testid="fb-seo-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-[#22C55E]" />
              <div className="font-semibold text-sm">Score SEO · {analysis.score}/100</div>
            </div>
            <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full text-white" style={{ background: color }}>
              {analysis.level === 'good' ? 'Ottimo' : analysis.level === 'ok' ? 'Migliorabile' : 'Debole'}
            </span>
          </div>

          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="Focus keyword (opzionale)"
            className="w-full mb-3 px-3 py-2 text-sm border border-neutral-300 rounded outline-none focus:border-[#22C55E]"
            data-testid="fb-seo-keyword"
          />

          <ul className="space-y-1.5 mb-4 max-h-40 overflow-y-auto">
            {analysis.hints.map((h, i) => (
              <li key={i} className="flex items-start gap-2 text-[12px]" data-testid={`fb-hint-${i}`}>
                <span className="mt-1 h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ background: h.level === 'good' ? '#22C55E' : h.level === 'ok' ? '#E0B400' : '#E24C4B' }} />
                <span className="text-neutral-700">{h.msg}</span>
              </li>
            ))}
          </ul>

          <div className="grid grid-cols-2 gap-2 mb-3">
            <button
              onClick={() => runAi('rewrite')}
              disabled={busy === 'rewrite' || !text.trim()}
              className="flex items-center justify-center gap-1.5 py-2 px-2 bg-[#22C55E] hover:bg-[#22C55E] text-white text-[10.5px] font-semibold uppercase tracking-[0.15em] transition disabled:opacity-60"
              data-testid="fb-ai-rewrite"
            >
              {busy === 'rewrite' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              Migliora testo
            </button>
            <button
              onClick={() => runAi('meta')}
              disabled={busy === 'meta' || !text.trim()}
              className="flex items-center justify-center gap-1.5 py-2 px-2 border border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E] hover:text-white text-[10.5px] font-semibold uppercase tracking-[0.15em] transition disabled:opacity-60"
              data-testid="fb-ai-meta"
            >
              {busy === 'meta' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              Meta title/desc
            </button>
          </div>

          {err && <div className="text-[11px] text-red-600 mb-2" data-testid="fb-ai-error">{err}</div>}

          {meta && (
            <div className="border-t border-neutral-200 pt-3 space-y-2" data-testid="fb-meta-result">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#22C55E] font-semibold mb-1">Meta title suggerito</div>
                <div className="text-[12.5px] text-neutral-800 p-2 bg-neutral-50 rounded border border-neutral-200 flex justify-between gap-2">
                  <span className="flex-1">{meta.title}</span>
                  <button onClick={() => navigator.clipboard?.writeText(meta.title || '')} className="text-[10px] text-[#22C55E] font-semibold uppercase tracking-wider">Copia</button>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#22C55E] font-semibold mb-1">Meta description suggerita</div>
                <div className="text-[12.5px] text-neutral-700 p-2 bg-neutral-50 rounded border border-neutral-200 flex justify-between gap-2">
                  <span className="flex-1">{meta.description}</span>
                  <button onClick={() => navigator.clipboard?.writeText(meta.description || '')} className="text-[10px] text-[#22C55E] font-semibold uppercase tracking-wider">Copia</button>
                </div>
              </div>
              <p className="text-[10px] text-neutral-500 italic">Copia e incolla in seoMeta.js — l'update automatico dei meta arriverà nella prossima release.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StatusInline = ({ status }) => {
  if (status === 'saving') return <span className="flex items-center gap-1 text-xs" data-testid="fb-status"><Loader2 className="h-3 w-3 animate-spin" /> Salvo…</span>;
  if (status === 'saved') return <span className="flex items-center gap-1 text-xs text-[#B4D97C]" data-testid="fb-status"><CheckCircle2 className="h-3 w-3" /> Salvato</span>;
  if (status === 'error') return <span className="text-xs text-red-300" data-testid="fb-status">Errore</span>;
  if (status === 'dirty') return <span className="text-xs text-white/70" data-testid="fb-status">Modificato</span>;
  return <span className="text-xs text-white/50" data-testid="fb-status">In modifica</span>;
};

// ---- Image modal (only for type=image) ----
const ImageModal = ({ config, onClose, onSaved }) => {
  const { section, path, value, label } = config;
  const [current, setCurrent] = useState(typeof value === 'string' ? value : '');
  const [uploading, setUploading] = useState(0);
  const [status, setStatus] = useState('idle');
  const inputRef = useRef(null);

  const onFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return alert('Seleziona un file immagine.');
    try {
      setUploading(10);
      const res = await uploadImage(file, (p) => setUploading(Math.max(10, p)));
      const url = buildMediaUrl(res.url);
      setCurrent(url);
      setUploading(0);
      setStatus('saving');
      const currentJson = (await fetchContent(section).catch(() => null)) || {};
      const patched = patchPath(currentJson, path, url);
      await saveContent(section, patched);
      setStatus('saved');
      onSaved && onSaved();
      setTimeout(() => setStatus('idle'), 1400);
    } catch (e) {
      setUploading(0);
      alert('Errore upload: ' + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="edit-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-2xl max-w-2xl w-full p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="font-serif-display text-2xl text-neutral-900" data-testid="modal-title">{label}</h3>
            <p className="text-xs text-neutral-500">Sezione: <code>{section}</code> · Campo: <code>{path}</code></p>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900" data-testid="modal-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="border-2 border-dashed border-neutral-300 rounded p-4 text-center" data-testid="image-dropzone"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) onFile(f); }}
        >
          {current && <img src={current} alt="preview" className="mx-auto mb-3 max-h-56 rounded shadow" data-testid="image-preview" />}
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="image-file-input" />
          <button
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#22C55E] hover:bg-[#1B2F1B] text-white text-xs font-semibold uppercase tracking-wider rounded transition"
            data-testid="image-upload-btn"
          >
            <UploadCloud className="h-4 w-4" /> {current ? 'Cambia immagine' : 'Carica immagine'}
          </button>
          <p className="text-[11px] text-neutral-500 mt-2">Trascina un file qui, oppure clicca (JPG · PNG · WebP · SVG)</p>
          {uploading > 0 && (
            <div className="mt-3 h-1 bg-neutral-200 rounded overflow-hidden" data-testid="upload-progress">
              <div className="h-full bg-[#22C55E] transition-all" style={{ width: `${uploading}%` }} />
            </div>
          )}
        </div>
        <div className="flex justify-end mt-4 items-center gap-3">
          <span className="text-xs text-neutral-500" data-testid="save-status">
            {status === 'saving' && <><Loader2 className="inline h-3 w-3 animate-spin mr-1" />Salvo…</>}
            {status === 'saved' && <><CheckCircle2 className="inline h-3 w-3 text-[#22C55E] mr-1" />Salvato</>}
          </span>
          <button onClick={onClose} className="px-4 py-2 border border-neutral-300 rounded text-sm hover:bg-neutral-50" data-testid="modal-cancel">Chiudi</button>
        </div>
      </div>
    </div>
  );
};

// Backwards-compat: some callers still use Save from adminApi via legacy modal — kept unused
// eslint-disable-next-line no-unused-vars
const _kept = Save;
